import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

export default function StickyMobileCTA({ label, to, href, event, testid = 'sticky-mobile-cta', hideOver }) {
  // Stay hidden over the hero so this never sits directly under an identical
  // in-page CTA; reveal once the visitor has scrolled past roughly one screen.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.7);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Optional: a selector for in-page forms. While any of them is on screen the
  // bar steps aside, so it never covers the form it would otherwise point to.
  const [covered, setCovered] = useState(false);
  useEffect(() => {
    if (!hideOver || typeof IntersectionObserver === 'undefined') return undefined;
    const targets = document.querySelectorAll(hideOver);
    if (!targets.length) return undefined;
    const onScreen = new Set();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => (e.isIntersecting ? onScreen.add(e.target) : onScreen.delete(e.target)));
      setCovered(onScreen.size > 0);
    });
    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, [hideOver]);

  const shown = scrolled && !covered;

  if (!label || (!to && !href)) return null;
  const inner = (
    <>
      {label}
      <ArrowUpRight className="h-4 w-4" />
    </>
  );
  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-40 md:hidden pointer-events-none transition-all duration-300 ${
        shown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 invisible'
      }`}
      aria-hidden={!shown}
    >
      <div className="p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] bg-gradient-to-t from-ink/90 via-ink/60 to-transparent">
        {href ? (
          <a
            href={href}
            data-testid={testid}
            onClick={() => event && trackEvent(event, { location: 'sticky_mobile' })}
            className="pointer-events-auto btn-accent w-full shadow-[0_12px_30px_rgba(5,5,7,0.45)]"
          >
            {inner}
          </a>
        ) : (
          <Link
            to={to}
            data-testid={testid}
            onClick={() => event && trackEvent(event, { location: 'sticky_mobile' })}
            className="pointer-events-auto btn-accent w-full shadow-[0_12px_30px_rgba(5,5,7,0.45)]"
          >
            {inner}
          </Link>
        )}
      </div>
    </div>
  );
}
